import { mediaQueryLarge } from '@theme/utilities';

/**
 * A media query for reduced motion preference. Kept local because
 * `@theme/utilities` only exposes the breakpoint query.
 * @type {MediaQueryList}
 */
const mediaQueryReducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Wraps a hero background `<video>` and controls playback in JS instead of
 * relying on the `autoplay` attribute.
 *
 * A muted, looped `<video>` with `autoplay` is still downloaded and still
 * plays in Chrome and Safari even when hidden with `display: none`, so every
 * mobile visitor would pay for the full download of a hero video they never
 * see. Loading the video with `preload="none"` and only switching to
 * `preload="auto"` plus calling `play()` once the desktop breakpoint and the
 * reduced-motion preference both allow it avoids that cost.
 *
 * With JavaScript disabled this element never upgrades: the video keeps
 * `preload="none"` and no `autoplay` attribute, so nothing plays and the
 * still background image shows instead, which is the correct degradation.
 */
class HeroBackgroundVideo extends HTMLElement {
  #controller = new AbortController();

  /** @returns {HTMLVideoElement | null} */
  get #video() {
    return this.querySelector('video');
  }

  connectedCallback() {
    const { signal } = this.#controller;

    mediaQueryLarge.addEventListener('change', this.#sync, { signal });
    mediaQueryReducedMotion.addEventListener('change', this.#sync, { signal });

    this.#sync();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Plays the video on desktop when motion is not reduced, pauses and stops
   * preloading otherwise.
   */
  #sync = () => {
    const video = this.#video;
    if (!video) return;

    const canAutoplay = mediaQueryLarge.matches && !mediaQueryReducedMotion.matches;

    if (canAutoplay) {
      video.preload = 'auto';
      // Autoplay can still be refused by the browser (e.g. data saver mode),
      // that rejection is expected and not an error worth surfacing.
      video.play().catch(() => {});
    } else {
      video.pause();
      video.preload = 'none';
    }
  };
}

if (!customElements.get('hero-background-video')) {
  customElements.define('hero-background-video', HeroBackgroundVideo);
}
